'use client';

import type { FC } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { m, useReducedMotion } from 'motion/react';

import type { TBoardTile } from '@/utils/auctions/auctionBoard';
import { proposalCardHover, proposalImageHover } from '@/utils/animation/variants';

import { CardAccentLine } from '../cards/shared/card-accent-line';

/**
 * One tile of the auction board (spec 014, research R4).
 *
 * The proposal card's language turned portrait: the photo is cut along its
 * BOTTOM edge (the wide card cuts the right one), the same gold accent line
 * draws on hover, the same lift-and-glow. One component renders both lanes —
 * only `fact` differs — so switching lanes reads as the
 * same cars changing state, not as two designs.
 *
 * Fixed size on purpose: the photo box is 3:4 at a fixed width and the text
 * block a fixed height, so a lane of one tile and a lane of ten are the same
 * height and the switch never moves the page (FR-016).
 */

type TAuctionBoardTile = {
  tile: TBoardTile;
  /** The first visible tile of the default lane is the only eager image. */
  priority?: boolean;
};

export const AuctionBoardTile: FC<TAuctionBoardTile> = ({ tile, priority = false }) => {
  const prefersReducedMotion = useReducedMotion();

  return (
    <m.article
      data-tile=""
      data-lane={tile.lane}
      data-id={tile.id}
      variants={prefersReducedMotion ? undefined : proposalCardHover}
      initial="rest"
      whileHover="hover"
      animate="rest"
      className="group relative h-full w-[220px] overflow-hidden rounded-xl border border-neutral-800 bg-backgroundLight transition-colors hover:border-action/30 md:w-[256px]"
    >
      <Link
        href={tile.href}
        data-lane={tile.lane}
        data-id={tile.id}
        className="flex h-full flex-col focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        <div className="relative aspect-[3/4] w-full overflow-hidden">
          <div className="absolute inset-0 [clip-path:polygon(0_0,100%_0,100%_86%,0_100%)]">
            <m.div variants={prefersReducedMotion ? undefined : proposalImageHover} className="h-full w-full">
              <Image
                src={tile.imageUrl}
                alt={tile.imageAlt}
                fill
                sizes="(max-width: 768px) 220px, 256px"
                priority={priority}
                // Straight from the auction house's CDN — see auction-lot-card.tsx.
                unoptimized
                className="object-cover"
              />
            </m.div>
          </div>

          <div className="pointer-events-none absolute inset-0 bg-automotive-gradient opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
        </div>

        <div className="relative flex h-[6.5rem] flex-col px-4 pb-4 pt-1">
          <span className="font-oswald text-xs uppercase tracking-[0.25em] text-neutral-500">{tile.year}</span>
          <span className="mt-0.5 line-clamp-2 font-oswald text-lg uppercase leading-tight text-foreground transition-colors group-hover:text-action">
            {tile.title}
          </span>
          <span className="mt-auto truncate pt-1 font-oswald text-xs uppercase tracking-[0.2em] text-action">
            {tile.fact}
          </span>
        </div>

        <CardAccentLine position="bottom" />
      </Link>
    </m.article>
  );
};
